/**
 * FancyKpi — Tarjeta KPI con estética inspirada en dashboards médicos
 * (ver screenshot de referencia Feb-2026).
 *
 *   ┌─────────────────────────────────┐
 *   │ Title              ⬤ icon       │
 *   │ 72Bpm   [Resting]               │  ← value grande + pill estado
 *   │ 3 hours ago                     │  ← timestamp
 *   │                                  │
 *   │ ╎╎┃┃╎┃╎  ─────────  mini chart   │  ← visualización
 *   │ 00.00                 24.00     │  ← axis helpers
 *   └─────────────────────────────────┘
 *
 * Props:
 *  - title     string
 *  - icon      Component (phosphor/lucide icon)
 *  - color     'red' | 'blue' | 'purple' | 'green' | 'orange' | 'violet'
 *  - value     string | number
 *  - pill      string                 (ej. "Resting", "Healthy", "Stable")
 *  - timestamp string                 (ej. "3 hours ago")
 *  - chart     ReactNode              (SVG o cualquier visualización mini)
 *  - axisLeft  string                 (opcional, ej. "00.00")
 *  - axisRight string                 (opcional, ej. "24.00")
 *  - testid    string
 *  - onClick   function               (opcional, convierte en botón)
 */

const COLOR_MAP = {
  red:    { bg: "bg-[#F05C62]", shadow: "shadow-[0_8px_18px_-6px_rgba(240,92,98,0.55)]" },
  blue:   { bg: "bg-[#5E6AE8]", shadow: "shadow-[0_8px_18px_-6px_rgba(94,106,232,0.55)]" },
  purple: { bg: "bg-[#B661F2]", shadow: "shadow-[0_8px_18px_-6px_rgba(182,97,242,0.55)]" },
  green:  { bg: "bg-[#2BC48A]", shadow: "shadow-[0_8px_18px_-6px_rgba(43,196,138,0.55)]" },
  orange: { bg: "bg-[#F58A4B]", shadow: "shadow-[0_8px_18px_-6px_rgba(245,138,75,0.55)]" },
  violet: { bg: "bg-[#8B7CFF]", shadow: "shadow-[0_8px_18px_-6px_rgba(139,124,255,0.55)]" },
};

export default function FancyKpi({
  title,
  icon: Icon,
  color = "blue",
  value,
  pill,
  timestamp,
  chart,
  axisLeft,
  axisRight,
  testid,
  onClick,
}) {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`relative text-left w-full bg-white rounded-[20px] p-3 flex flex-col min-h-[110px]
        shadow-[0_1px_4px_rgba(0,0,0,0.03),0_8px_20px_-12px_rgba(0,0,0,0.08)]
        ${onClick ? "hover:shadow-[0_2px_8px_rgba(0,0,0,0.05),0_12px_28px_-14px_rgba(0,0,0,0.12)] active:scale-[0.995] transition" : ""}
      `}
    >
      {/* Header row — title + colored icon */}
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11.5px] font-medium text-black/55 tracking-tight truncate">
          {title}
        </div>
        {Icon && (
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${c.bg} ${c.shadow}`}>
            <Icon
              className="w-[13px] h-[13px] text-white"
              strokeWidth={1.75}
              weight="regular"
            />
          </div>
        )}
      </div>

      {/* Value + pill */}
      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
        <span className="text-[16px] font-bold text-black tracking-tight leading-none">
          {value}
        </span>
        {pill && (
          <span className="inline-flex items-center bg-black/[0.045] text-black/70 text-[9.5px] font-semibold rounded-full px-1.5 py-0.5 whitespace-nowrap">
            {pill}
          </span>
        )}
      </div>

      {/* Timestamp */}
      {timestamp && (
        <div className="mt-0.5 text-[10px] text-black/45 tracking-tight truncate">
          {timestamp}
        </div>
      )}

      {/* Mini chart area */}
      <div className="mt-auto pt-1.5">
        {chart && <div className="w-full h-[24px]">{chart}</div>}
        {(axisLeft || axisRight) && (
          <div className="flex items-center justify-between mt-0.5 text-[8.5px] text-black/35 font-medium tabular-nums">
            <span>{axisLeft}</span>
            <span>{axisRight}</span>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Visualizaciones mini (SVG) listas para pasar como `chart`.
 * Todas se auto-escalan al contenedor (viewBox + preserveAspectRatio=none).
 * ───────────────────────────────────────────────────────────── */

/**
 * SparkBars — Mini barras verticales (series de 1 número cada una).
 * data: array de números, color: hex para la barra activa.
 */
export function SparkBars({ data = [], color = "#2BC48A", muted = "#E5E5EA" }) {
  if (data.length === 0) data = [0.3, 0.6, 0.9];
  const max = Math.max(1, ...data);
  const n = Math.max(data.length, 18);
  const barWidth = 100 / (n * 1.6);
  const gap = barWidth * 0.6;
  return (
    <svg
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      className="w-full h-full"
      aria-hidden="true"
    >
      {Array.from({ length: n }).map((_, i) => {
        const v = data[i] ?? 0;
        const h = v > 0 ? Math.max(4, (v / max) * 36) : 2;
        const x = i * (barWidth + gap);
        const y = 40 - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={h}
            rx={1}
            fill={v > 0 ? color : muted}
          />
        );
      })}
    </svg>
  );
}

/**
 * SparkLine — Mini área con línea. Suaviza con comandos cubic.
 * Opcional: dots=true dibuja puntos sobre cada vértice (estilo blood sugar).
 */
export function SparkLine({ data = [], stroke = "#5E6AE8", fillOpacity = 0.15, dots = false }) {
  if (data.length < 2) data = [0.3, 0.5, 0.4, 0.6, 0.55, 0.7];
  const max = Math.max(1, ...data);
  const min = Math.min(0, ...data);
  const range = max - min || 1;
  const step = 100 / (data.length - 1);
  const points = data.map((v, i) => [
    i * step,
    38 - ((v - min) / range) * 34,
  ]);
  const path = points
    .map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`))
    .join(" ");
  const area = path + ` L100,40 L0,40 Z`;
  return (
    <svg
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      className="w-full h-full"
      aria-hidden="true"
    >
      <path d={area} fill={stroke} fillOpacity={fillOpacity} />
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {dots &&
        points.map((p, i) => (
          <circle
            key={i}
            cx={p[0]}
            cy={p[1]}
            r={0.9}
            fill={stroke}
            vectorEffect="non-scaling-stroke"
          />
        ))}
    </svg>
  );
}

/**
 * SparkArea — Área sólida tipo "waveform" (Blood Pressure del referencia).
 * Siempre normaliza la data para que la onda sea visible, incluso cuando
 * la variación numérica es pequeña.
 */
export function SparkArea({ data = [], color = "#F05C62", fillOpacity = 0.75 }) {
  // Si hay menos de 2 puntos, generamos un patrón sutil de onda plana
  if (data.length < 2) data = [0.9, 1.1, 1.0, 1.15, 1.05, 0.95, 1.0];
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = 100 / (data.length - 1);
  // Mapeamos a [6, 34] para dejar un margen arriba y abajo
  const ys = data.map((v) => 34 - ((v - min) / range) * 28);
  const points = data.map((_, i) => [i * step, ys[i]]);
  const path = points
    .map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`))
    .join(" ");
  const area = path + ` L100,40 L0,40 Z`;
  const gid = `sparkArea-${color.replace("#", "")}`;
  return (
    <svg
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      className="w-full h-full"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={fillOpacity} />
          <stop offset="100%" stopColor={color} stopOpacity={fillOpacity * 0.25} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * SparkCandles — Mini "candlestick" (barras finas pareadas tipo OHLC).
 * data: array de números. Para cada punto dibuja un stem fino que cubre
 * aproximadamente ±delta alrededor del valor, con color de acento si el
 * valor es > 0 o gris si es 0 (slot sin dato).
 */
export function SparkCandles({ data = [], color = "#F05C62", muted = "#E5E5EA" }) {
  const n = Math.max(data.length, 20);
  const points = Array.from({ length: n }, (_, i) => data[i] ?? 0);
  const max = Math.max(1, ...points);
  const slot = 100 / n;
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full" aria-hidden="true">
      {points.map((v, i) => {
        const cx = (i + 0.5) * slot;
        if (v === 0) {
          // Slot vacío — fina barra gris completa
          return (
            <rect
              key={i}
              x={cx - 0.6}
              y={10}
              width={1.2}
              height={20}
              rx={0.6}
              fill={muted}
            />
          );
        }
        const h = Math.max(6, (v / max) * 28);
        const y = (40 - h) / 2;
        return (
          <rect
            key={i}
            x={cx - 1.1}
            y={y}
            width={2.2}
            height={h}
            rx={1.1}
            fill={color}
          />
        );
      })}
    </svg>
  );
}

/**
 * SparkDotGrid — Grid 2 filas × N columnas de círculos (como Temperature).
 * Implementado con flex de divs para que los círculos siempre sean redondos
 * (SVG con preserveAspectRatio=none los deforma en ovalados).
 */
export function SparkDotGrid({ value = 0, total = 10, color = "#F58A4B", muted = "#E5E5EA" }) {
  const cols = Math.ceil(total / 2);
  return (
    <div className="w-full h-full flex flex-col justify-between py-0.5" aria-hidden="true">
      {[0, 1].map((row) => (
        <div key={row} className="flex items-center justify-between gap-1 w-full">
          {Array.from({ length: cols }).map((_, c) => {
            const i = row * cols + c;
            if (i >= total) return <span key={c} className="w-2 h-2" />;
            const active = i < value;
            return (
              <span
                key={c}
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{
                  background: active ? color : muted,
                  opacity: active ? 1 : 0.7,
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * SparkDots — Fila de dots que marcan intensidad (semáforo discreto).
 * value: número activo (0-4), color: hex del "ON".
 */
export function SparkDots({ value = 0, total = 5, color = "#F58A4B" }) {
  return (
    <svg
      viewBox="0 0 100 40"
      preserveAspectRatio="none"
      className="w-full h-full"
      aria-hidden="true"
    >
      {Array.from({ length: total }).map((_, i) => {
        const cx = (i + 0.5) * (100 / total);
        return (
          <circle
            key={i}
            cx={cx}
            cy={20}
            r={9}
            fill={i < value ? color : "#E5E5EA"}
            opacity={i < value ? 1 : 0.7}
          />
        );
      })}
    </svg>
  );
}

/**
 * SparkPill — Barra de rango con un marcador (slider style, como BMI/Normal).
 * value: 0-1, color: hex del marcador.
 */
export function SparkPill({ value = 0.5, color = "#2BC48A", label }) {
  const pct = Math.max(0, Math.min(1, value));
  return (
    <div className="relative w-full h-full flex items-center">
      <div className="w-full flex items-center gap-1">
        {[0.1, 0.3, 0.5, 0.7, 0.9].map((p, i) => {
          const active = Math.abs(p - pct) <= 0.1;
          return (
            <div
              key={i}
              className="flex-1 h-2.5 rounded-full"
              style={{
                background: active ? color : "#E5E5EA",
                opacity: active ? 1 : 0.7,
              }}
            />
          );
        })}
      </div>
      {label && (
        <div
          className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] font-semibold rounded-full px-2.5 py-0.5"
        >
          {label}
        </div>
      )}
    </div>
  );
}
